import { useState } from "react";
import { supabase } from "../lib/supabase";
import { C } from "../constants/colors";

// Gallery-side modal: confirm acceptance and create the consignment agreement.
// Only creates an agreement row when opportunity.consignment_offered is true.
export function ConsignmentConfirmModal({ app, galleryId, onClose, onDone }) {
  const opp = app.opportunities || {};
  const hasConsignment = Boolean(opp.consignment_offered);
  const artistName = app.artist?.display_name || app.artist?.full_name || "Artist";

  // Pre-fill from first submitted artwork when available
  const firstArtwork = (() => {
    try { return JSON.parse(app.image_captions || "{}").artworks?.[0] || {}; }
    catch { return {}; }
  })();

  const [artworkTitle, setArtworkTitle] = useState(firstArtwork.title || "");
  const [artworkValue, setArtworkValue] = useState(() => {
    const raw = String(firstArtwork.price || "").replace(/[^0-9.]/g, "");
    return raw || "";
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const inp = { background:C.parch,border:`1px solid ${C.sand}`,borderRadius:6,padding:"9px 11px",fontSize:".83rem",color:C.bark,width:"100%",boxSizing:"border-box",outline:"none",fontFamily:"inherit" };
  const lbl = { fontSize:".62rem",fontFamily:"'DM Mono',monospace",letterSpacing:"1px",textTransform:"uppercase",color:C.muted,display:"block",marginBottom:4 };

  const handleConfirm = async () => {
    if (hasConsignment) {
      if (!artworkTitle.trim()) { setError("Please enter the artwork title."); return; }
      const val = parseFloat(artworkValue);
      if (!artworkValue || isNaN(val) || val < 0) { setError("Please enter a valid artwork value."); return; }
    }
    setSaving(true);
    setError(null);

    const { error: appErr } = await supabase
      .from("applications")
      .update({ status: "accepted", updated_at: new Date().toISOString() })
      .eq("id", app.id);

    if (appErr) { setError(appErr.message); setSaving(false); return; }

    if (hasConsignment) {
      const { error: agrErr } = await supabase
        .from("consignment_agreements")
        .insert({
          opportunity_id: app.opportunity_id,
          application_id: app.id,
          artist_id: app.artist_id,
          gallery_id: galleryId,
          artwork_title: artworkTitle.trim(),
          artwork_value: parseFloat(artworkValue),
          commission: opp.commission ? parseFloat(opp.commission) : null,
          consignment_duration: opp.consignment_duration || null,
          damage_policy: opp.damage_policy || null,
          return_shipping: opp.return_shipping || null,
          payment_terms: opp.payment_terms || null,
          status: "pending_artist_review",
          gallery_confirmed: true,
          gallery_confirmed_at: new Date().toISOString(),
        });
      if (agrErr) { setError(agrErr.message); setSaving(false); return; }
    }

    setSaving(false);
    onDone();
  };

  return (
    <div
      style={{ position:"fixed",inset:0,zIndex:300,display:"flex",alignItems:"flex-start",justifyContent:"center",background:"rgba(40,30,20,.65)",overflowY:"auto",padding:"24px 16px" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background:C.parch,borderRadius:12,width:"100%",maxWidth:520,padding:"28px 24px",boxShadow:"0 8px 40px rgba(0,0,0,.22)" }} onClick={e=>e.stopPropagation()}>

        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20 }}>
          <h2 style={{ fontFamily:"'Fraunces',serif",fontSize:"1.3rem",fontWeight:400,color:C.bark,margin:0 }}>Confirm acceptance</h2>
          <button onClick={onClose} style={{ background:"none",border:"none",fontSize:"1.2rem",cursor:"pointer",color:C.muted,lineHeight:1,padding:4 }}>✕</button>
        </div>

        <p style={{ fontSize:".86rem",color:C.mutedD,lineHeight:1.65,marginBottom:hasConsignment ? 20 : 24 }}>
          You're accepting <strong style={{ color:C.bark }}>{artistName}</strong>'s application.
          {hasConsignment && " The consignment terms below will be sent to the artist for their review and acknowledgement."}
        </p>

        {hasConsignment && (
          <>
            <div style={{ background:C.cream,border:`1px solid ${C.sand}`,borderRadius:8,padding:"16px",marginBottom:20 }}>
              <span className="slbl" style={{ marginBottom:12 }}>Consignment terms</span>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8 }}>
                {[
                  ["Commission", opp.commission || "—"],
                  ["Duration", opp.consignment_duration ? `${opp.consignment_duration} weeks` : "—"],
                  ["Damage policy", opp.damage_policy || "—"],
                  ["Return shipping", opp.return_shipping || "—"],
                ].map(([k, v]) => (
                  <div key={k} style={{ background:C.parch,border:`1px solid ${C.parchDD}`,borderRadius:5,padding:"9px 11px" }}>
                    <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".54rem",letterSpacing:"1.5px",textTransform:"uppercase",color:C.muted,marginBottom:3 }}>{k}</div>
                    <div style={{ fontSize:".82rem",color:C.bark,fontWeight:500,textTransform:"capitalize" }}>{v}</div>
                  </div>
                ))}
              </div>
              {opp.payment_terms && (
                <div style={{ background:C.parch,border:`1px solid ${C.parchDD}`,borderRadius:5,padding:"9px 11px",marginBottom:8 }}>
                  <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".54rem",letterSpacing:"1.5px",textTransform:"uppercase",color:C.muted,marginBottom:3 }}>Payment terms</div>
                  <div style={{ fontSize:".82rem",color:C.bark }}>{opp.payment_terms}</div>
                </div>
              )}
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={lbl}>Artwork title *</label>
              <input style={inp} value={artworkTitle} onChange={e=>setArtworkTitle(e.target.value)} placeholder="e.g. Untitled No. 3"/>
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={lbl}>Artwork value (AUD) *</label>
              <input style={inp} type="number" min="0" step="1" value={artworkValue} onChange={e=>setArtworkValue(e.target.value)} placeholder="e.g. 1800"/>
            </div>
          </>
        )}

        {error && (
          <div style={{ background:C.roseL,border:`1px solid ${C.rose}`,borderRadius:5,padding:"10px 14px",marginBottom:16,fontSize:".8rem",color:C.rose }}>⚠ {error}</div>
        )}

        <div style={{ display:"flex",gap:10 }}>
          <button className="btn-dk" style={{ flex:1,padding:"12px",fontSize:".7rem" }} onClick={onClose} disabled={saving}>Cancel</button>
          <button
            onClick={handleConfirm}
            disabled={saving}
            style={{ flex:2,padding:"12px",fontSize:".7rem",borderRadius:6,border:"none",background:C.sageL,color:C.sageD,cursor:saving?"wait":"pointer",fontFamily:"'DM Mono',monospace",letterSpacing:"1px",textTransform:"uppercase",fontWeight:600,opacity:saving?0.7:1 }}
          >
            {saving ? "Saving…" : "Confirm acceptance →"}
          </button>
        </div>

      </div>
    </div>
  );
}
